import { filter } from '@atlaskit/adf-utils/traverse';

import { expValEquals } from '@confluence/feature-experiments';
import { contributorsPreloadHandler } from './handlers/contributorsPreloadHandler.ts';

/**
 * An ADF extension node representing a macro instance in the document.
 */
export interface MacroContentNode {
	type: string;
	attrs?: {
		extensionKey?: string;
		parameters?: {
			macroParams?: Record<string, { value: string }>;
		};
	};
}

/**
 * Context passed to each macro preload handler, containing information
 * about the content being preloaded.
 */
export interface MacroPreloadContext {
	contentId: string;
	spaceKey?: string;
}

/**
 * A handler that knows how to preload data for a specific macro type.
 *
 * Each macro that supports preloading should implement this interface and
 * register its handler with `preloadMacroData`.
 */
export interface MacroPreloadHandler {
	/**
	 * The ADF extension key that identifies this macro type
	 * (e.g. 'contributors', 'detailssummary').
	 */
	extensionKey: string;

	/**
	 * Preloads data for a single instance of this macro found in the content.
	 *
	 * @param node - The ADF extension node matching this handler's extensionKey
	 * @param context - Content context (contentId, spaceKey, etc.)
	 * @returns A promise that resolves when preloading is complete
	 */
	preload: (node: MacroContentNode, context: MacroPreloadContext) => Promise<void>;
}

const EXTENSION_TYPES = new Set(['extension', 'bodiedExtension', 'inlineExtension']);

/**
 * Returns a map of extensionKey → handler for all active macro preload
 * handlers based on current experiment gates.
 */
const getActiveHandlers = (): Map<string, MacroPreloadHandler> => {
	const handlers = new Map<string, MacroPreloadHandler>();

	if (expValEquals('cc_rewrite_contributors_legacy_macro', 'isEnabled', true)) {
		handlers.set(contributorsPreloadHandler.extensionKey, contributorsPreloadHandler);
	}

	return handlers;
};

/**
 * Resolves content nodes (which may be a promise), validates the body structure,
 * and parses the ADF JSON into a document object.
 *
 * Returns null if the content nodes are invalid, the representation is not
 * 'atlas_doc_format', or the ADF JSON cannot be parsed.
 */
const resolveAndParseADF = async (contentNodes: any): Promise<any | null> => {
	const resolvedNodes =
		contentNodes && typeof contentNodes.then === 'function' ? await contentNodes : contentNodes;

	if (!resolvedNodes?.[0]?.body?.dynamic?.value) {
		return null;
	}

	if (resolvedNodes[0].body.dynamic.representation !== 'atlas_doc_format') {
		return null;
	}

	try {
		return JSON.parse(resolvedNodes[0].body.dynamic.value);
	} catch {
		return null;
	}
};

/**
 * Filters extension nodes from the parsed ADF document that have a
 * registered preload handler.
 */
const getPreloadableMacroNodes = (
	documentADF: any,
	handlersByKey: Map<string, MacroPreloadHandler>,
): MacroContentNode[] => {
	return filter(documentADF, (node) => {
		return (
			EXTENSION_TYPES.has(node.type) &&
			typeof node.attrs?.extensionKey === 'string' &&
			handlersByKey.has(node.attrs.extensionKey)
		);
	}) as MacroContentNode[];
};

/**
 * Preloads data for all registered macro types found in the content.
 *
 * This function centralizes the macro preloading pattern by:
 * 1. Determining which macro handlers are active (based on experiment gates)
 * 2. Resolving content nodes (which may be a raw value or a Promise)
 * 3. Parsing the ADF document once
 * 4. Filtering for extension nodes that have a registered handler
 * 5. Calling the corresponding handler for each matched node in parallel
 *
 * @param params.contentNodes - The raw content nodes (or a Promise resolving to them)
 * @param params.context - Content context passed through to each handler
 */
export const preloadMacroData = async ({
	contentNodes,
	context,
}: {
	contentNodes: any;
	context: MacroPreloadContext;
}): Promise<void> => {
	const handlersByKey = getActiveHandlers();
	if (handlersByKey.size === 0) {
		return;
	}

	const documentADF = await resolveAndParseADF(contentNodes);
	if (!documentADF) {
		return;
	}

	const matchedNodes = getPreloadableMacroNodes(documentADF, handlersByKey);
	if (matchedNodes.length === 0) {
		return;
	}

	// eslint-disable-next-line check-preloaders/no-promise-all
	await Promise.all(
		matchedNodes.map((node) => {
			const handler = handlersByKey.get(node.attrs!.extensionKey!)!;
			return handler.preload(node, context);
		}),
	);
};
